'use client';

import { createClient } from '@/lib/supabase/client';
import { withOfflineQueue } from '@/lib/offlineWrapper';

// ─── Helper ───────────────────────────────────────────────────────────────────

function isSchemaError(error: any): boolean {
  if (!error) return false;
  if (error.code && typeof error.code === 'string') {
    const cls = error.code.substring(0, 2);
    if (cls === '42' || cls === '08') return true;
    if (cls === '23') return false;
  }
  if (error.message) {
    return /relation.*does not exist|column.*does not exist|function.*does not exist|syntax error|type.*does not exist/i.test(error.message);
  }
  return false;
}

async function safeQuery<T>(fn: () => Promise<{ data: T | null; error: any }>): Promise<T | null> {
  try {
    const { data, error } = await fn();
    if (error) {
      if (isSchemaError(error)) throw error;
      console.warn('[supabase-service]', error.message);
      return null;
    }
    return data;
  } catch (e: any) {
    if (isSchemaError(e)) throw e;
    console.warn('[supabase-service]', e?.message);
    return null;
  }
}

// ─── Auth helpers ─────────────────────────────────────────────────────────────

export async function getAuthUser() {
  const supabase = createClient();
  const { data: { user } } = await supabase.auth.getUser();
  return user;
}

// ─── Tasks / Agenda ───────────────────────────────────────────────────────────

export const tasksService = {
  async getAll(userId: string) {
    const supabase = createClient();
    const data = await safeQuery(() =>
      supabase.from('tasks').select('*').eq('user_id', userId).order('created_at', { ascending: false })
    );
    return (data as any[]) || [];
  },
  async upsert(task: any, userId?: string) {
    const supabase = createClient();
    const uid = userId || task.user_id;
    return withOfflineQueue(
      { type: task.id ? 'update' : 'create', table: 'tasks', module: 'Agenda', recordId: task.id, data: task, userId: uid, updatedAt: task.updatedAt || task.updated_at },
      () => supabase.from('tasks').upsert(task, { onConflict: 'id' }).select().single()
    );
  },
  async delete(id: string, userId?: string) {
    const supabase = createClient();
    return withOfflineQueue(
      { type: 'delete', table: 'tasks', module: 'Agenda', recordId: id, data: { id }, userId: userId || '' },
      () => supabase.from('tasks').delete().eq('id', id)
    );
  },
};

// ─── Finances / Transactions ──────────────────────────────────────────────────

export const transactionsService = {
  async getAll(userId: string) {
    const supabase = createClient();
    // ingresos
    const inc = await safeQuery(() =>
      supabase.from('ingresos').select('*').eq('user_id', userId).order('created_at', { ascending: false })
    ) as any[] | null;
    // egresos
    const exp = await safeQuery(() =>
      supabase.from('egresos').select('*').eq('user_id', userId).order('created_at', { ascending: false })
    ) as any[] | null;

    const incomes = (inc || []).map((r: any) => ({
      id: r.id, type: 'ingreso' as const, date: r.fecha, concept: r.descripcion,
      amount: Number(r.monto), category: r.categoria, business: '', account: '',
      paymentMethod: r.metodo, description: r.descripcion, observations: r.observaciones,
      createdAt: r.created_at,
    }));
    const expenses = (exp || []).map((r: any) => ({
      id: r.id, type: 'gasto' as const, date: r.fecha, concept: r.descripcion,
      amount: Number(r.monto), category: r.categoria, business: '', account: '',
      paymentMethod: r.metodo, description: r.descripcion, observations: r.observaciones,
      createdAt: r.created_at,
    }));
    return [...incomes, ...expenses];
  },
  async create(tx: any, userId: string) {
    const supabase = createClient();
    const table = tx.type === 'ingreso' ? 'ingresos' : 'egresos';
    const payload = {
      id: tx.id, user_id: userId, fecha: tx.date, categoria: tx.category,
      descripcion: tx.concept, monto: tx.amount, metodo: tx.paymentMethod || 'efectivo',
      observaciones: tx.observations || '', usuario: '',
    };
    return withOfflineQueue(
      { type: 'create', table, module: tx.type === 'ingreso' ? 'Ingresos' : 'Gastos', recordId: tx.id, data: payload, userId },
      () => supabase.from(table).insert(payload).select().single()
    );
  },
  async delete(id: string, type: 'ingreso' | 'gasto', userId?: string) {
    const supabase = createClient();
    const table = type === 'ingreso' ? 'ingresos' : 'egresos';
    return withOfflineQueue(
      { type: 'delete', table, module: type === 'ingreso' ? 'Ingresos' : 'Gastos', recordId: id, data: { id }, userId: userId || '' },
      () => supabase.from(table).delete().eq('id', id)
    );
  },
  async update(id: string, tx: any, type: 'ingreso' | 'gasto', userId?: string) {
    const supabase = createClient();
    const table = type === 'ingreso' ? 'ingresos' : 'egresos';
    const payload = {
      fecha: tx.date, categoria: tx.category, descripcion: tx.concept,
      monto: tx.amount, metodo: tx.paymentMethod || 'efectivo', observaciones: tx.observations || '',
    };
    return withOfflineQueue(
      { type: 'update', table, module: type === 'ingreso' ? 'Ingresos' : 'Gastos', recordId: id, data: { id, ...payload, user_id: userId }, userId: userId || '' },
      () => supabase.from(table).update(payload).eq('id', id).select().single()
    );
  },
};

// ─── Accounts / Cuentas ───────────────────────────────────────────────────────

export const accountsService = {
  async getAll(userId: string) {
    const supabase = createClient();
    const data = await safeQuery(() =>
      supabase.from('cuentas_financieras').select('*').eq('user_id', userId).order('created_at', { ascending: false })
    ) as any[] | null;
    return (data || []).map((r: any) => ({
      id: r.id, name: r.nombre, type: r.tipo, initialBalance: Number(r.saldo_inicial),
      balance: Number(r.saldo_actual), currency: r.moneda, description: r.descripcion,
      status: r.estado, createdAt: r.created_at,
    }));
  },
  async create(acc: any, userId: string) {
    const supabase = createClient();
    const payload = {
      id: acc.id, user_id: userId, nombre: acc.name, tipo: acc.type,
      saldo_inicial: acc.initialBalance, saldo_actual: acc.balance || acc.initialBalance,
      moneda: acc.currency || 'HNL', descripcion: acc.description || '',
      estado: acc.status || 'activa', fecha_creacion: acc.createdAt || new Date().toISOString(),
      institucion: acc.name,
    };
    return withOfflineQueue(
      { type: 'create', table: 'cuentas_financieras', module: 'Cuentas', recordId: acc.id, data: payload, userId },
      () => supabase.from('cuentas_financieras').insert(payload).select().single()
    );
  },
  async update(id: string, acc: any, userId?: string) {
    const supabase = createClient();
    const payload = {
      nombre: acc.name, tipo: acc.type, saldo_inicial: acc.initialBalance,
      saldo_actual: acc.balance, moneda: acc.currency, descripcion: acc.description,
      estado: acc.status,
    };
    return withOfflineQueue(
      { type: 'update', table: 'cuentas_financieras', module: 'Cuentas', recordId: id, data: { id, ...payload, user_id: userId }, userId: userId || '' },
      () => supabase.from('cuentas_financieras').update(payload).eq('id', id).select().single()
    );
  },
  async delete(id: string, userId?: string) {
    const supabase = createClient();
    return withOfflineQueue(
      { type: 'delete', table: 'cuentas_financieras', module: 'Cuentas', recordId: id, data: { id }, userId: userId || '' },
      () => supabase.from('cuentas_financieras').delete().eq('id', id)
    );
  },
};

// ─── Clients / Préstamos ──────────────────────────────────────────────────────

export const clientsService = {
  async getAll(userId: string) {
    const supabase = createClient();
    const data = await safeQuery(() =>
      supabase.from('clientes').select('*').eq('user_id', userId).eq('eliminado', false).order('created_at', { ascending: false })
    ) as any[] | null;
    return (data || []).map((r: any) => ({
      id: r.id, code: r.id, name: r.nombre, phone: r.telefono, whatsapp: r.whatsapp,
      email: '', address: r.direccion, idNumber: r.identidad, birthDate: '',
      registeredAt: r.fecha_registro, status: r.estado === 'activo' ? 'activo' : r.estado,
      totalLoans: r.prestamos_activos || 0, activeLoans: r.prestamos_activos || 0,
      totalDebt: Number(r.saldo_pendiente) || 0, observations: r.observaciones || '',
    }));
  },
  async create(client: any, userId: string) {
    const supabase = createClient();
    const payload = {
      id: client.id, user_id: userId, nombre: client.name, identidad: client.idNumber || '',
      telefono: client.phone, whatsapp: client.whatsapp || '', direccion: client.address || '',
      estado: 'activo', fecha_registro: client.registeredAt || new Date().toISOString().split('T')[0],
      observaciones: client.observations || '',
    };
    return withOfflineQueue(
      { type: 'create', table: 'clientes', module: 'Clientes', recordId: client.id, data: payload, userId },
      () => supabase.from('clientes').insert(payload).select().single()
    );
  },
  async update(id: string, client: any, userId?: string) {
    const supabase = createClient();
    const payload = {
      nombre: client.name, identidad: client.idNumber || '', telefono: client.phone,
      whatsapp: client.whatsapp || '', direccion: client.address || '',
      observaciones: client.observations || '',
    };
    return withOfflineQueue(
      { type: 'update', table: 'clientes', module: 'Clientes', recordId: id, data: { id, ...payload, user_id: userId }, userId: userId || '' },
      () => supabase.from('clientes').update(payload).eq('id', id).select().single()
    );
  },
  async delete(id: string, userId?: string) {
    const supabase = createClient();
    return withOfflineQueue(
      { type: 'update', table: 'clientes', module: 'Clientes', recordId: id, data: { id, eliminado: true }, userId: userId || '' },
      () => supabase.from('clientes').update({ eliminado: true }).eq('id', id)
    );
  },
};

// ─── Loans / Préstamos ────────────────────────────────────────────────────────

export const loansService = {
  async getAll(userId: string) {
    const supabase = createClient();
    const data = await safeQuery(() =>
      supabase.from('prestamos').select('*').eq('user_id', userId).order('created_at', { ascending: false })
    ) as any[] | null;
    return (data || []).map((r: any) => ({
      id: r.id, code: r.numero, clientId: r.cliente_id, clientName: r.cliente,
      capital: Number(r.capital), loanDate: r.fecha_prestamo, interestRate: Number(r.tasa_interes),
      interestType: r.tipo_interes || 'mensual', frequency: r.frecuencia, quotaCount: r.cuotas_total,
      interest: Number(r.interes), capitalPaid: Number(r.pagado), capitalPending: Number(r.saldo),
      quota: Number(r.cuota), mora: Number(r.mora), multa: Number(r.multa),
      paymentDate: r.proximo_pago, nextPayment: r.proximo_pago, startPaymentDate: r.fecha_inicio_pago || '',
      paymentDay: 0, amortizationMethod: r.sistema_amortizacion || 'frances',
      status: r.estado, observations: r.observaciones || '',
    }));
  },
  async create(loan: any, userId: string) {
    const supabase = createClient();
    const payload = {
      id: loan.id, user_id: userId, numero: loan.code, cliente_id: loan.clientId,
      cliente: loan.clientName, capital: loan.capital, tasa_interes: loan.interestRate,
      interes: loan.interest, total: loan.capital + loan.interest, cuota: loan.quota,
      pagado: 0, saldo: loan.capitalPending, proximo_pago: loan.nextPayment,
      estado: loan.status, fecha_prestamo: loan.loanDate, frecuencia: loan.frequency,
      cuotas_total: loan.quotaCount, cuotas_pagadas: 0, tipo_interes: loan.interestType,
      fecha_inicio_pago: loan.startPaymentDate, sistema_amortizacion: loan.amortizationMethod,
      observaciones: loan.observations || '', cuotas: '[]',
    };
    return withOfflineQueue(
      { type: 'create', table: 'prestamos', module: 'Préstamos', recordId: loan.id, data: payload, userId },
      () => supabase.from('prestamos').insert(payload).select().single()
    );
  },
  async update(id: string, loan: any, userId?: string) {
    const supabase = createClient();
    const payload = {
      saldo: loan.capitalPending, pagado: loan.capitalPaid, mora: loan.mora,
      multa: loan.multa, estado: loan.status, proximo_pago: loan.nextPayment,
      cuotas_pagadas: loan.cuotasPagadas,
    };
    return withOfflineQueue(
      { type: 'update', table: 'prestamos', module: 'Préstamos', recordId: id, data: { id, ...payload, user_id: userId }, userId: userId || '' },
      () => supabase.from('prestamos').update(payload).eq('id', id).select().single()
    );
  },
  async delete(id: string, userId?: string) {
    const supabase = createClient();
    return withOfflineQueue(
      { type: 'delete', table: 'prestamos', module: 'Préstamos', recordId: id, data: { id }, userId: userId || '' },
      () => supabase.from('prestamos').delete().eq('id', id)
    );
  },
};

// ─── Loan Payments ────────────────────────────────────────────────────────────

export const paymentsService = {
  async getAll(userId: string) {
    const supabase = createClient();
    const data = await safeQuery(() =>
      supabase.from('pagos').select('*').eq('user_id', userId).order('created_at', { ascending: false })
    ) as any[] | null;
    return (data || []).map((r: any) => ({
      id: r.id, loanId: r.prestamo_id, loanCode: r.numero_prestamo, clientName: r.cliente,
      date: r.fecha, capital: Number(r.capital), interest: Number(r.interes),
      mora: Number(r.mora), multa: Number(r.multa), total: Number(r.total),
      observations: r.observaciones || '',
    }));
  },
  async create(payment: any, userId: string) {
    const supabase = createClient();
    return safeQuery(() =>
      supabase.from('pagos').insert({
        id: payment.id, user_id: userId, prestamo_id: payment.loanId,
        cliente_id: '', cliente: payment.clientName, numero_prestamo: payment.loanCode,
        numero_cuota: 0, fecha: payment.date, capital: payment.capital,
        interes: payment.interest, mora: payment.mora, multa: payment.multa,
        total: payment.total, metodo_pago: 'efectivo', observaciones: payment.observations || '',
        usuario: '', saldo_antes: 0, saldo_despues: 0,
      }).select().single()
    );
  },
};

// ─── Followups ────────────────────────────────────────────────────────────────

export const followupsService = {
  async getAll(userId: string) {
    const supabase = createClient();
    const data = await safeQuery(() =>
      supabase.from('acciones_cobranza').select('*').eq('user_id', userId).order('created_at', { ascending: false })
    ) as any[] | null;
    return (data || []).map((r: any) => ({
      id: r.id, clientId: r.cliente_id, clientName: '', loanId: r.prestamo_id,
      loanCode: '', date: r.fecha, type: r.tipo, status: '', result: r.observaciones,
      nextFollowup: r.promesa_fecha || '', nextAction: '', observations: r.observaciones,
    }));
  },
  async create(f: any, userId: string) {
    const supabase = createClient();
    return safeQuery(() =>
      supabase.from('acciones_cobranza').insert({
        id: f.id, user_id: userId, cliente_id: f.clientId, prestamo_id: f.loanId || '',
        tipo: f.type, fecha: f.date, hora: '', usuario: '', observaciones: f.observations || '',
      }).select().single()
    );
  },
  async delete(id: string) {
    const supabase = createClient();
    return safeQuery(() => supabase.from('acciones_cobranza').delete().eq('id', id));
  },
};

// ─── EGA Studio Clients ───────────────────────────────────────────────────────

export const egaStudioService = {
  async getAll(userId: string) {
    const supabase = createClient();
    const data = await safeQuery(() =>
      supabase.from('ega_studio_clients').select('*').eq('user_id', userId).order('created_at', { ascending: false })
    ) as any[] | null;
    return (data || []).map((r: any) => ({
      id: r.id, name: r.name, idNumber: r.id_number, phone: r.phone, whatsapp: r.whatsapp,
      email: r.email, address: r.address, facebook: r.facebook, instagram: r.instagram,
      tiktok: r.tiktok, clientType: r.client_type, service: r.service,
      contactDate: r.contact_date, eventDate: r.event_date, price: Number(r.price),
      advance: Number(r.advance), balance: Number(r.balance), status: r.status,
      nextFollowup: r.next_followup, observations: r.observations, createdAt: r.created_at,
    }));
  },
  async upsert(client: any, userId: string) {
    const supabase = createClient();
    const payload = {
      id: client.id, user_id: userId, name: client.name, id_number: client.idNumber || '',
      phone: client.phone, whatsapp: client.whatsapp || '', email: client.email || '',
      address: client.address || '', facebook: client.facebook || '',
      instagram: client.instagram || '', tiktok: client.tiktok || '',
      client_type: client.clientType || '', service: client.service || '',
      contact_date: client.contactDate || '', event_date: client.eventDate || '',
      price: client.price || 0, advance: client.advance || 0, balance: client.balance || 0,
      status: client.status || 'prospecto', next_followup: client.nextFollowup || '',
      observations: client.observations || '',
    };
    return withOfflineQueue(
      { type: client.id ? 'update' : 'create', table: 'ega_studio_clients', module: 'EGA Studio', recordId: client.id, data: payload, userId },
      () => supabase.from('ega_studio_clients').upsert(payload, { onConflict: 'id' }).select().single()
    );
  },
  async delete(id: string, userId?: string) {
    const supabase = createClient();
    return withOfflineQueue(
      { type: 'delete', table: 'ega_studio_clients', module: 'EGA Studio', recordId: id, data: { id }, userId: userId || '' },
      () => supabase.from('ega_studio_clients').delete().eq('id', id)
    );
  },
};

// ─── Songs ────────────────────────────────────────────────────────────────────

export const songsService = {
  async getAll(userId: string) {
    const supabase = createClient();
    const data = await safeQuery(() =>
      supabase.from('songs').select('*').eq('user_id', userId).order('created_at', { ascending: false })
    ) as any[] | null;
    return (data || []).map((r: any) => ({
      id: r.id, title: r.title, artist: r.artist, genre: r.genre, album: r.album,
      releaseType: r.release_type, createdDate: r.created_date, launchDate: r.launch_date,
      composer: r.composer, producer: r.producer, duration: r.duration, status: r.status,
      coverUrl: r.cover_url, lyrics: r.lyrics, audioUrl: r.audio_url,
      platforms: r.platforms || [], observations: r.observations, createdAt: r.created_at,
    }));
  },
  async upsert(song: any, userId: string) {
    const supabase = createClient();
    const payload = {
      id: song.id, user_id: userId, title: song.title, artist: song.artist || '',
      genre: song.genre || '', album: song.album || '', release_type: song.releaseType || '',
      created_date: song.createdDate || '', launch_date: song.launchDate || '',
      composer: song.composer || '', producer: song.producer || '', duration: song.duration || '',
      status: song.status || 'idea', cover_url: song.coverUrl || '', lyrics: song.lyrics || '',
      audio_url: song.audioUrl || '', platforms: song.platforms || [], observations: song.observations || '',
    };
    return withOfflineQueue(
      { type: song.id ? 'update' : 'create', table: 'songs', module: 'EGA Music', recordId: song.id, data: payload, userId },
      () => supabase.from('songs').upsert(payload, { onConflict: 'id' }).select().single()
    );
  },
  async delete(id: string, userId?: string) {
    const supabase = createClient();
    return withOfflineQueue(
      { type: 'delete', table: 'songs', module: 'EGA Music', recordId: id, data: { id }, userId: userId || '' },
      () => supabase.from('songs').delete().eq('id', id)
    );
  },
};

// ─── Products ─────────────────────────────────────────────────────────────────

export const productsService = {
  async getAll(userId: string) {
    const supabase = createClient();
    const data = await safeQuery(() =>
      supabase.from('products').select('*').eq('user_id', userId).order('created_at', { ascending: false })
    ) as any[] | null;
    return (data || []).map((r: any) => ({
      id: r.id, name: r.name, code: r.code, category: r.category, description: r.description,
      cost: Number(r.cost), normalPrice: Number(r.normal_price), prePrice: Number(r.pre_price),
      resellerPrice: Number(r.reseller_price), stock: r.stock, unit: r.unit,
      provider: r.provider, imageUrl: r.image_url, active: r.active, notes: r.notes,
      createdAt: r.created_at,
    }));
  },
  async upsert(product: any, userId: string) {
    const supabase = createClient();
    const payload = {
      id: product.id, user_id: userId, name: product.name, code: product.code || '',
      category: product.category || '', description: product.description || '',
      cost: product.cost || 0, normal_price: product.normalPrice || 0,
      pre_price: product.prePrice || 0, reseller_price: product.resellerPrice || 0,
      stock: product.stock || 0, unit: product.unit || 'unidad',
      provider: product.provider || '', image_url: product.imageUrl || '',
      active: product.active !== false, notes: product.notes || '',
    };
    return withOfflineQueue(
      { type: product.id ? 'update' : 'create', table: 'products', module: 'Ventas', recordId: product.id, data: payload, userId },
      () => supabase.from('products').upsert(payload, { onConflict: 'id' }).select().single()
    );
  },
  async delete(id: string, userId?: string) {
    const supabase = createClient();
    return withOfflineQueue(
      { type: 'delete', table: 'products', module: 'Ventas', recordId: id, data: { id }, userId: userId || '' },
      () => supabase.from('products').delete().eq('id', id)
    );
  },
};

// ─── Orders ───────────────────────────────────────────────────────────────────

export const ordersService = {
  async getAll(userId: string) {
    const supabase = createClient();
    const data = await safeQuery(() =>
      supabase.from('orders').select('*').eq('user_id', userId).order('created_at', { ascending: false })
    ) as any[] | null;
    return (data || []).map((r: any) => ({
      id: r.id, code: r.code, clientName: r.client_name, clientPhone: r.client_phone,
      items: r.items || [], total: Number(r.total), totalCost: Number(r.total_cost),
      profit: Number(r.profit), saleType: r.sale_type, paymentMethod: r.payment_method,
      status: r.status, date: r.fecha, observations: r.observations, createdAt: r.created_at,
    }));
  },
  async upsert(order: any, userId: string) {
    const supabase = createClient();
    const payload = {
      id: order.id, user_id: userId, code: order.code, client_name: order.clientName || '',
      client_phone: order.clientPhone || '', items: order.items || [],
      total: order.total || 0, total_cost: order.totalCost || 0, profit: order.profit || 0,
      sale_type: order.saleType || 'normal', payment_method: order.paymentMethod || 'efectivo',
      status: order.status || 'pendiente', fecha: order.date || '', observations: order.observations || '',
    };
    return withOfflineQueue(
      { type: order.id ? 'update' : 'create', table: 'orders', module: 'Ventas', recordId: order.id, data: payload, userId },
      () => supabase.from('orders').upsert(payload, { onConflict: 'id' }).select().single()
    );
  },
  async delete(id: string, userId?: string) {
    const supabase = createClient();
    return withOfflineQueue(
      { type: 'delete', table: 'orders', module: 'Ventas', recordId: id, data: { id }, userId: userId || '' },
      () => supabase.from('orders').delete().eq('id', id)
    );
  },
};

// ─── Books ────────────────────────────────────────────────────────────────────

export const booksService = {
  async getAll(userId: string) {
    const supabase = createClient();
    const data = await safeQuery(() =>
      supabase.from('books').select('*').eq('user_id', userId).order('created_at', { ascending: false })
    ) as any[] | null;
    return (data || []).map((r: any) => ({
      id: r.id, title: r.title, author: r.author, isbn: r.isbn, category: r.category,
      editorial: r.editorial, publisher: r.publisher, status: r.status,
      startDate: r.start_date, endDate: r.end_date, purchaseDate: r.purchase_date,
      price: Number(r.price), stock: r.stock, format: r.format, rating: r.rating,
      summary: r.summary, notes: r.notes, lessons: r.lessons, coverUrl: r.cover_url,
      createdAt: r.created_at,
    }));
  },
  async upsert(book: any, userId: string) {
    const supabase = createClient();
    const payload = {
      id: book.id, user_id: userId, title: book.title, author: book.author || '',
      isbn: book.isbn || '', category: book.category || '', editorial: book.editorial || '',
      publisher: book.publisher || '', status: book.status || 'pendiente',
      start_date: book.startDate || '', end_date: book.endDate || '',
      purchase_date: book.purchaseDate || '', price: book.price || 0, stock: book.stock || 0,
      format: book.format || '', rating: book.rating || 0, summary: book.summary || '',
      notes: book.notes || '', lessons: book.lessons || '', cover_url: book.coverUrl || '',
    };
    return withOfflineQueue(
      { type: book.id ? 'update' : 'create', table: 'books', module: 'Libros', recordId: book.id, data: payload, userId },
      () => supabase.from('books').upsert(payload, { onConflict: 'id' }).select().single()
    );
  },
  async delete(id: string, userId?: string) {
    const supabase = createClient();
    return withOfflineQueue(
      { type: 'delete', table: 'books', module: 'Libros', recordId: id, data: { id }, userId: userId || '' },
      () => supabase.from('books').delete().eq('id', id)
    );
  },
};

// ─── Habits ───────────────────────────────────────────────────────────────────

export const habitsService = {
  async getAll(userId: string) {
    const supabase = createClient();
    const data = await safeQuery(() =>
      supabase.from('habits').select('*').eq('user_id', userId).order('created_at', { ascending: false })
    ) as any[] | null;
    return (data || []).map((r: any) => ({
      id: r.id, name: r.name, description: r.description, category: r.category,
      frequency: r.frequency, days: r.days || [], time: r.time, goal: r.goal,
      startDate: r.start_date, status: r.status, streak: r.streak, bestStreak: r.best_streak,
      completedToday: r.completed_today, totalCompleted: r.total_completed,
      completionRate: r.completion_rate, logs: r.logs || [], createdAt: r.created_at,
    }));
  },
  async upsert(habit: any, userId: string) {
    const supabase = createClient();
    const payload = {
      id: habit.id, user_id: userId, name: habit.name, description: habit.description || '',
      category: habit.category || '', frequency: habit.frequency || 'diario',
      days: habit.days || [], time: habit.time || '', goal: habit.goal || 1,
      start_date: habit.startDate || '', status: habit.status || 'activo',
      streak: habit.streak || 0, best_streak: habit.bestStreak || 0,
      completed_today: habit.completedToday || false, total_completed: habit.totalCompleted || 0,
      completion_rate: habit.completionRate || 0, logs: habit.logs || [],
    };
    return withOfflineQueue(
      { type: habit.id ? 'update' : 'create', table: 'habits', module: 'Hábitos', recordId: habit.id, data: payload, userId },
      () => supabase.from('habits').upsert(payload, { onConflict: 'id' }).select().single()
    );
  },
  async delete(id: string, userId?: string) {
    const supabase = createClient();
    return withOfflineQueue(
      { type: 'delete', table: 'habits', module: 'Hábitos', recordId: id, data: { id }, userId: userId || '' },
      () => supabase.from('habits').delete().eq('id', id)
    );
  },
};

// ─── Health Records ───────────────────────────────────────────────────────────

export const healthService = {
  async getAll(userId: string) {
    const supabase = createClient();
    const data = await safeQuery(() =>
      supabase.from('health_records').select('*').eq('user_id', userId).order('created_at', { ascending: false })
    ) as any[] | null;
    return (data || []).map((r: any) => ({
      id: r.id, date: r.fecha, weight: r.weight, height: r.height,
      physicalActivity: r.physical_activity, activityDuration: r.activity_duration,
      diet: r.diet, waist: r.waist, hip: r.hip, chest: r.chest, goal: r.goal,
      type: r.type, time: r.time, description: r.description, doctor: r.doctor,
      clinic: r.clinic, notes: r.notes, reminder: r.reminder, reminderDate: r.reminder_date,
      createdAt: r.created_at,
    }));
  },
  async upsert(record: any, userId: string) {
    const supabase = createClient();
    const payload = {
      id: record.id, user_id: userId, fecha: record.date, weight: record.weight,
      height: record.height, physical_activity: record.physicalActivity || '',
      activity_duration: record.activityDuration, diet: record.diet || '',
      waist: record.waist, hip: record.hip, chest: record.chest, goal: record.goal || '',
      type: record.type || '', time: record.time || '', description: record.description || '',
      doctor: record.doctor || '', clinic: record.clinic || '', notes: record.notes || '',
      reminder: record.reminder || false, reminder_date: record.reminderDate || '',
    };
    return withOfflineQueue(
      { type: record.id ? 'update' : 'create', table: 'health_records', module: 'Salud', recordId: record.id, data: payload, userId },
      () => supabase.from('health_records').upsert(payload, { onConflict: 'id' }).select().single()
    );
  },
  async delete(id: string, userId?: string) {
    const supabase = createClient();
    return withOfflineQueue(
      { type: 'delete', table: 'health_records', module: 'Salud', recordId: id, data: { id }, userId: userId || '' },
      () => supabase.from('health_records').delete().eq('id', id)
    );
  },
};

// ─── Businesses ───────────────────────────────────────────────────────────────

export const businessesService = {
  async getAll(userId: string) {
    const supabase = createClient();
    const data = await safeQuery(() =>
      supabase.from('businesses').select('*').eq('user_id', userId).order('created_at', { ascending: false })
    ) as any[] | null;
    return (data || []).map((r: any) => ({
      id: r.id, name: r.name, logoUrl: r.logo_url, description: r.description,
      category: r.category, phone: r.phone, whatsapp: r.whatsapp, email: r.email,
      address: r.address, website: r.website, facebook: r.facebook, instagram: r.instagram,
      tiktok: r.tiktok, youtube: r.youtube, startDate: r.start_date,
      income: Number(r.income), expenses: Number(r.expenses), profit: Number(r.profit),
      clients: r.clients, status: r.status, goals: r.goals, notes: r.notes,
      createdAt: r.created_at,
    }));
  },
  async upsert(biz: any, userId: string) {
    const supabase = createClient();
    const payload = {
      id: biz.id, user_id: userId, name: biz.name, logo_url: biz.logoUrl || '',
      description: biz.description || '', category: biz.category || '',
      phone: biz.phone || '', whatsapp: biz.whatsapp || '', email: biz.email || '',
      address: biz.address || '', website: biz.website || '', facebook: biz.facebook || '',
      instagram: biz.instagram || '', tiktok: biz.tiktok || '', youtube: biz.youtube || '',
      start_date: biz.startDate || '', income: biz.income || 0, expenses: biz.expenses || 0,
      profit: biz.profit || 0, clients: biz.clients || 0, status: biz.status || 'activo',
      goals: biz.goals || '', notes: biz.notes || '',
    };
    return withOfflineQueue(
      { type: biz.id ? 'update' : 'create', table: 'businesses', module: 'Mis Negocios', recordId: biz.id, data: payload, userId },
      () => supabase.from('businesses').upsert(payload, { onConflict: 'id' }).select().single()
    );
  },
  async delete(id: string, userId?: string) {
    const supabase = createClient();
    return withOfflineQueue(
      { type: 'delete', table: 'businesses', module: 'Mis Negocios', recordId: id, data: { id }, userId: userId || '' },
      () => supabase.from('businesses').delete().eq('id', id)
    );
  },
};

// ─── Business Ideas ───────────────────────────────────────────────────────────

export const ideasService = {
  async getAll(userId: string) {
    const supabase = createClient();
    const data = await safeQuery(() =>
      supabase.from('business_ideas').select('*').eq('user_id', userId).order('created_at', { ascending: false })
    ) as any[] | null;
    return (data || []).map((r: any) => ({
      id: r.id, name: r.name, description: r.description, category: r.category,
      problemSolved: r.problem_solved, targetAudience: r.target_audience,
      investment: Number(r.investment), estimatedIncome: Number(r.estimated_income),
      estimatedProfit: Number(r.estimated_profit), risk: r.risk, timeRequired: r.time_required,
      demand: r.demand, status: r.status, priority: r.priority, date: r.fecha,
      score: r.score, observations: r.observations, createdAt: r.created_at,
    }));
  },
  async upsert(idea: any, userId: string) {
    const supabase = createClient();
    const payload = {
      id: idea.id, user_id: userId, name: idea.name, description: idea.description || '',
      category: idea.category || '', problem_solved: idea.problemSolved || '',
      target_audience: idea.targetAudience || '', investment: idea.investment || 0,
      estimated_income: idea.estimatedIncome || 0, estimated_profit: idea.estimatedProfit || 0,
      risk: idea.risk || '', time_required: idea.timeRequired || '', demand: idea.demand || '',
      status: idea.status || 'idea', priority: idea.priority || 'media',
      fecha: idea.date || '', score: idea.score || 0, observations: idea.observations || '',
    };
    return withOfflineQueue(
      { type: idea.id ? 'update' : 'create', table: 'business_ideas', module: 'Nuevos Negocios', recordId: idea.id, data: payload, userId },
      () => supabase.from('business_ideas').upsert(payload, { onConflict: 'id' }).select().single()
    );
  },
  async delete(id: string, userId?: string) {
    const supabase = createClient();
    return withOfflineQueue(
      { type: 'delete', table: 'business_ideas', module: 'Nuevos Negocios', recordId: id, data: { id }, userId: userId || '' },
      () => supabase.from('business_ideas').delete().eq('id', id)
    );
  },
};

// ─── Productive Activities ────────────────────────────────────────────────────

export const productiveActivitiesService = {
  async getAll(userId: string) {
    const supabase = createClient();
    const data = await safeQuery(() =>
      supabase.from('productive_activities').select('*').eq('user_id', userId).order('created_at', { ascending: false })
    ) as any[] | null;
    return (data || []).map((r: any) => ({
      id: r.id, name: r.name, description: r.description, category: r.category,
      priority: r.priority, generatesIncome: r.generates_income, dailyGoal: r.daily_goal,
      measurableUnit: r.measurable_unit, goalTarget: r.goal_target,
      scheduledTime: r.scheduled_time, estimatedMinutes: r.estimated_minutes,
      frequency: r.frequency, specificDays: r.specific_days || [], active: r.active,
      createdAt: r.created_at, updatedAt: r.updated_at,
    }));
  },
  async upsert(activity: any, userId: string) {
    const supabase = createClient();
    const payload = {
      id: activity.id, user_id: userId, name: activity.name, description: activity.description || '',
      category: activity.category || 'Personal', priority: activity.priority || 'media',
      generates_income: activity.generatesIncome || false, daily_goal: activity.dailyGoal || '',
      measurable_unit: activity.measurableUnit || '', goal_target: activity.goalTarget || 0,
      scheduled_time: activity.scheduledTime || '', estimated_minutes: activity.estimatedMinutes || 30,
      frequency: activity.frequency || 'diario', specific_days: activity.specificDays || [],
      active: activity.active !== false,
    };
    return withOfflineQueue(
      { type: activity.id ? 'update' : 'create', table: 'productive_activities', module: 'Agenda Productiva', recordId: activity.id, data: payload, userId },
      () => supabase.from('productive_activities').upsert(payload, { onConflict: 'id' }).select().single()
    );
  },
  async delete(id: string, userId?: string) {
    const supabase = createClient();
    return withOfflineQueue(
      { type: 'delete', table: 'productive_activities', module: 'Agenda Productiva', recordId: id, data: { id }, userId: userId || '' },
      () => supabase.from('productive_activities').delete().eq('id', id)
    );
  },
};

// ─── Daily Productive Records ─────────────────────────────────────────────────

export const dailyRecordsService = {
  async getAll(userId: string) {
    const supabase = createClient();
    const data = await safeQuery(() =>
      supabase.from('daily_productive_records').select('*').eq('user_id', userId).order('fecha', { ascending: false })
    ) as any[] | null;
    return (data || []).map((r: any) => ({
      id: r.id, activityId: r.activity_id, activityName: r.activity_name,
      category: r.category, priority: r.priority, date: r.fecha, status: r.status,
      completed: r.completed, goalTarget: r.goal_target, measurableUnit: r.measurable_unit,
      result: r.result, completionPercent: r.completion_percent, scheduledTime: r.scheduled_time,
      startTime: r.start_time, endTime: r.end_time, minutesDedicated: r.minutes_dedicated,
      generatesIncome: r.generates_income, incomeGenerated: Number(r.income_generated),
      expectedIncome: Number(r.expected_income), observations: r.observations,
      createdAt: r.created_at, updatedAt: r.updated_at,
    }));
  },
  async upsert(record: any, userId: string) {
    const supabase = createClient();
    const payload = {
      id: record.id, user_id: userId, activity_id: record.activityId,
      activity_name: record.activityName || '', category: record.category || '',
      priority: record.priority || 'media', fecha: record.date, status: record.status || 'pendiente',
      completed: record.completed || false, goal_target: record.goalTarget || 0,
      measurable_unit: record.measurableUnit || '', result: record.result || 0,
      completion_percent: record.completionPercent || 0, scheduled_time: record.scheduledTime || '',
      start_time: record.startTime || '', end_time: record.endTime || '',
      minutes_dedicated: record.minutesDedicated || 0, generates_income: record.generatesIncome || false,
      income_generated: record.incomeGenerated || 0, expected_income: record.expectedIncome || 0,
      observations: record.observations || '',
    };
    return withOfflineQueue(
      { type: record.id ? 'update' : 'create', table: 'daily_productive_records', module: 'Agenda Productiva', recordId: record.id, data: payload, userId },
      () => supabase.from('daily_productive_records').upsert(payload, { onConflict: 'id' }).select().single()
    );
  },
  async delete(id: string, userId?: string) {
    const supabase = createClient();
    return withOfflineQueue(
      { type: 'delete', table: 'daily_productive_records', module: 'Agenda Productiva', recordId: id, data: { id }, userId: userId || '' },
      () => supabase.from('daily_productive_records').delete().eq('id', id)
    );
  },
};

// ─── App Config (theme, profile) ─────────────────────────────────────────────

export const appConfigService = {
  async get(userId: string) {
    const supabase = createClient();
    const data = await safeQuery(() =>
      supabase.from('app_config').select('*').eq('user_id', userId).maybeSingle()
    ) as any | null;
    return data?.config_data || {};
  },
  async save(userId: string, config: any) {
    const supabase = createClient();
    return safeQuery(() =>
      supabase.from('app_config').upsert({ user_id: userId, config_data: config }, { onConflict: 'user_id' }).select().single()
    );
  },
};

// ─── User Profile ─────────────────────────────────────────────────────────────

export const userProfileService = {
  async get(userId: string) {
    const supabase = createClient();
    return safeQuery(() =>
      supabase.from('user_profiles').select('*').eq('id', userId).single()
    );
  },
  async update(userId: string, data: any) {
    const supabase = createClient();
    return safeQuery(() =>
      supabase.from('user_profiles').update({
        full_name: data.full_name || data.name,
        phone: data.phone || '',
        whatsapp: data.whatsapp || '',
        business_name: data.business_name || data.company || '',
        address: data.address || '',
        logo_url: data.logo_url || data.logoUrl || '',
      }).eq('id', userId).select().single()
    );
  },
};
